import React, { useEffect, useRef, useState } from 'react';
import { View, Text, ScrollView, TouchableOpacity, Image, ProgressBar } from 'react-native-web';
import AwesomeIcon from 'react-native-vector-icons/dist/FontAwesome';
import MaterialIcon from 'react-native-vector-icons/dist/MaterialIcons';
import * as Animatable from 'react-native-animatable';
import Lightbox from 'yet-another-react-lightbox';
import { cloneDeep } from 'lodash';
import moment from 'moment';
import SwipeableViews from 'react-swipeable-views';
import StarRatings from 'react-star-ratings/build/star-ratings';
import Captions from 'yet-another-react-lightbox/plugins/captions';
import * as Braze from '@braze/web-sdk';

// Child Components
import BrazeBanner from './component_braze_banner';
import EditNameModal from './component_name_edit';

// Custom Components
import LoadingView from '../../components/component_loading_view';

// Utils
import { getCoverPhoto, getCuisineText, getLogoPhoto, getProfileTabWarningMessage, getRatingValue } from '../../utils/util_truck_helpers';
import { formatPhoneNumber, getSocialLinkID, showAlert } from '../../utils/util_helpers';
import { getNextEventInstance } from '../../utils/util_date_checker';

// APIs
import { getAlbumPhotos, getFoodTruckCalendar, getOverviewMenu, getTruckDetail, getTruckReviews } from '../../modules';

// Configs
import GLOBAL from '../../configs/config_global';
import { getGlobalState, setGlobalState, STATE_KEYS, useGlobalState } from '../../configs/config_state';

// Navigation
import * as RootNavigation from '../../navigation/RootNavigation';
import routeKeys from '../../navigation/RouteKeys';
import { processDeepLink } from '../../utils/util_deep_link';
import { useRoute } from '@react-navigation/native';
import RouteKeys from '../../navigation/RouteKeys';
import SavedPayment from '../AccountSettings/component_saved_payment';

export function Profile() {
    // region Data
    const warningMessageData = getProfileTabWarningMessage();
    // endregion

    // region Route Params
    const { goTo = '' } = useRoute()?.params || {};
    // endregion

    // region Refs
    const truckDataLoadedBefore = useRef(false);
    const offDateString = useRef({
        2: 'Your truck is on vacay until\n[date]',
        3: 'Your truck is undergoing maintenance until\n[date]',
        4: 'Your truck is in hibernation until\n[date]'
    });
    const brazeBanner = useRef(null);
    const editNameModal = useRef(null);
    const savedPaymentModal = useRef(null);
    // endregion

    // region Global State
    const [globalTruckData, setGlobalTruckData] = useGlobalState(STATE_KEYS.TRUCK_DATA);
    const [profileRefreshTriggered, setProfileRefreshTriggered] = useGlobalState(STATE_KEYS.PROFILE_REFRESH_TRIGGERED);
    const [currentScreenKey] = useGlobalState(STATE_KEYS.CURRENT_SCREEN_KEY);
    // endregion

    // region Local State
    const [truckData, setTruckData] = useState({});
    const [truckCalendar, setTruckCalendar] = useState([]);
    const [isLoading, setLoading] = useState(true);
    const [isLoadingCalendar, setLoadingCalendar] = useState(true);
    const [isLoadingCalendarOnly, setLoadingCalendarOnly] = useState(false);
    const [calendarError, setCalendarError] = useState(false);
    const [currentCalendarIndex, setCurrentCalendarIndex] = useState(0);
    const [isLoadingPhotos, setLoadingPhotos] = useState(true);
    const [photoData, setPhotoData] = useState([]);
    const [photoError, setPhotoError] = useState(null);
    const [totalPhotoResult, setTotalPhotoResult] = useState(0);
    const [photoCarouselOpened, setPhotoCarouselOpened] = useState(false);
    const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);
    const [isLoadingMenuOverview, setLoadingMenuOverview] = useState(true);
    const [menuOverviewData, setMenuOverviewData] = useState([]);
    const [menuOverviewError, setMenuOverviewError] = useState(null);
    const [currentMenuIndex, setCurrentMenuIndex] = useState(0);
    const [isLoadingReviews, setLoadingReviews] = useState(true);
    const [unreadReviewCount, setUnreadReviewCount] = useState(0);
    const [reviewData, setReviewData] = useState([]);
    const [totalReviewCount, setTotalReviewCount] = useState(0);
    const [reviewError, setReviewError] = useState(null);
    const [errorMessage, setErrorMessage] = useState(null);
    const [currentLogo, setCurrentLogo] = useState(null);
    const [logoOpened, setLogoOpened] = useState(false);
    const [currentCover, setCurrentCover] = useState(null);
    const [coverOpened, setCoverOpened] = useState(false);
    // endregion

    // region Function - Fetch Truck Detail
    const fetchTruckDetail = (reloadProfileOnly = false) => {
        setLoading(true);
        getTruckDetail(globalTruckData.id)
            .then((result) => {
                // Save data to state
                const newTruckData = Object.assign({}, result, {
                    facebook_url: result.facebook_url ? getSocialLinkID(result.facebook_url) : '',
                    twitter_url: result.twitter_url ? getSocialLinkID(result.twitter_url) : '',
                    instagram_url: result.instagram_url ? getSocialLinkID(result.instagram_url) : ''
                });
                setTruckData(newTruckData);
                setCurrentLogo(getLogoPhoto(result, null, true));
                setCurrentCover(getCoverPhoto(result, null, true));
                setLoading(false);
                setErrorMessage(null);

                // Update truck data in global
                setGlobalTruckData(Object.assign(cloneDeep(globalTruckData), result));

                // Handle deep link
                const deepLinkData = getGlobalState(STATE_KEYS.DEEP_LINK_DATA);
                processDeepLink(deepLinkData);

                // Load other data
                setTimeout(() => {
                    truckDataLoadedBefore.current = true;
                    if (!reloadProfileOnly) {
                        setTimeout(() => goFetchCalendar(result), 1000);
                        getTruckPhotos(result);
                        getMenuOverview(result);
                        goFetchReviews(result);
                    }

                    // Grab Braze Content Cards
                    brazeBanner.current?.refreshCardList();
                }, 500);
            })
            .catch((error) => {
                setLoading(false);
                setLoadingPhotos(false);
                setLoadingCalendar(false);
                setLoadingCalendarOnly(false);
                setLoadingMenuOverview(false);
                setLoadingReviews(false);
                setErrorMessage(error);
            });
    }
    // endregion

    // region Function - Get Truck Calendar
    const goFetchCalendar = (newTruckData = null) => {
        const usingTruckData = newTruckData || truckData || {};
        setLoadingCalendar(true);
        getFoodTruckCalendar(usingTruckData.id)
            .then((result) => {
                // Sort calendar in order to detect multiple-day events
                const calendarArr = result.data
                    .map((item) => Object.assign(cloneDeep(item), { timeObj: getNextEventInstance(item) }))
                    .filter((item) => item.timeObj && (item.timeObj.endTime >= moment().unix()))
                    .sort((a, b) => a.timeObj.startTime - b.timeObj.startTime);

                // Save to state
                setTruckData(Object.assign(cloneDeep(usingTruckData), { calendar: result.data }));
                setTruckCalendar(calendarArr);
                setLoadingCalendar(false);
                setCalendarError(null);
            })
            .catch((error) => {
                setLoadingCalendar(false);
                setCalendarError(error.message);
            });
    }
    // endregion

    // region Function - Get Truck Photos
    const getTruckPhotos = (newTruckData = null) => {
        const usingTruckData = newTruckData || truckData || {};
        if (usingTruckData['albums']?.length > 0) {
            setLoadingPhotos(true);
            getAlbumPhotos(usingTruckData.id, usingTruckData['albums'][0].id, 1, 9)
                .then((result) => {
                    setLoadingPhotos(false);
                    setPhotoError(null);
                    setPhotoData(
                        (result.data || [])
                            .map((item, index) => ({
                                key: `imageItem_${item.id}`,
                                src: item['path_webp'] || item['path'],
                                id: item.id,
                                title: `Truck Photo ${index + 1} of ${result['totalResult']}`,
                                description: 'To view all photos, go Truck Photo Album'
                            }))
                            .sort((a, b) => Number(a['position']) - Number(b['position']))
                    );
                    setTotalPhotoResult(result['totalResult']);
                })
                .catch((error) => {
                    setLoadingPhotos(false);
                    setPhotoError(error);
                    setPhotoData([]);
                    setTotalPhotoResult(0);
                });
        }
    }
    // endregion

    // region Function - Get Truck Menu Overview
    const getMenuOverview = (newTruckData = null) => {
        const usingTruckData = newTruckData || truckData || {};
        setLoadingMenuOverview(true);
        getOverviewMenu(usingTruckData.id, 1, 20)
            .then((result) => {
                setMenuOverviewData(result.data);
                setLoadingMenuOverview(false);
                setMenuOverviewError(null);
            })
            .catch((error) => {
                setMenuOverviewData([]);
                setLoadingMenuOverview(false);
                setMenuOverviewError(error);
            });
    }
    // endregion

    // region Function - Fetch Review Data
    const goFetchReviews = () => {
        setLoadingReviews(true);
        getTruckReviews(1, 3)
            .then((reviewData) => {
                setUnreadReviewCount(reviewData['unreadCount']);
                setReviewData((reviewData['data'] || []).filter((item) => !!item?.comment));
                setTotalReviewCount(reviewData['totalResult'] || 0);
                setLoadingReviews(false);
                setReviewError(null);
            })
            .catch((error) => {
                setUnreadReviewCount(0);
                setReviewData([]);
                setTotalReviewCount(0);
                setLoadingReviews(false);
                setReviewError(error);
            });
    }
    // endregion

    // region Function - On Edit Profile Pressed
    const _onEditProfilePressed = () => {
        RootNavigation.navigate(routeKeys.PROFILE_EDIT, {
            truckData,
            onProfileUpdated: () => fetchTruckDetail(true)
        });
    };
    // endregion

    // region Function - On Schedule Pressed
    const _onSchedulePressed = (shouldOpenTimeOff = false) => {
        RootNavigation.navigate(routeKeys.PROFILE_SCHEDULE, {
            shouldOpenTimeOff,
            onTimeOffUpdated: (newTruckData) => setTruckData(newTruckData),
            onUpdated: () => goFetchCalendar()
        });
    };
    // endregion

    // region Function - On Menu Pressed
    const _onMenuPressed = () => {
        RootNavigation.navigate(routeKeys.MENU);
    };
    // endregion

    // region Function - On Album Pressed
    const _onAlbumPressed = () => {
        RootNavigation.navigate(routeKeys.PROFILE_EDIT_PHOTOS, {
            truckData,
            photoAlbumID: !!truckData['albums'].length ? truckData['albums']?.find((item) => item.name.toLowerCase() === 'default')?.id : -1,
            onPhotoAlbumUpdated: (newPhotoData, newTotalCount) => {
                setPhotoData(newPhotoData.slice(0, 3));
                setTotalPhotoResult(newTotalCount);
            }
        });
    };
    // endregion

    // region Function - On Review Pressed
    const _onReviewPressed = () => {
        RootNavigation.navigate(routeKeys.REVIEWS, {
            onReplyRead: (newUnreadCount) => setUnreadReviewCount(newUnreadCount)
        });
    };
    // endregion

    // region Component Mounting
    useEffect(() => {
        fetchTruckDetail();
    }, []);

    useEffect(() => {
        if (currentScreenKey.includes('Profile-')) {
            setGlobalState(STATE_KEYS.PROFILE_SCREEN_KEY, currentScreenKey);
        }
    }, [currentScreenKey]);
    // endregion

    // region Effect - Refresh Trigger
    useEffect(() => {
        if (profileRefreshTriggered) {
            setProfileRefreshTriggered(false);
            fetchTruckDetail();
        }
    }, [profileRefreshTriggered]);
    // endregion

    // region Effect - Deep Link Watch
    useEffect(() => {
        if (!goTo?.trim()) {
            return;
        }

        const goToLink = goTo;

        // Reset params
        RootNavigation.setParams(getGlobalState(STATE_KEYS.PROFILE_SCREEN_KEY), { goTo: '' });

        // Go to the requested link
        switch(goToLink) {
            case 'edit-profile': {
                _onEditProfilePressed();
                break;
            }
            case 'edit-schedule': {
                _onSchedulePressed();
                break;
            }
            case 'all-reviews': {
                _onReviewPressed();
                break;
            }
            case 'menu': {
                _onMenuPressed();
                break;
            }
            case 'photos': {
                _onAlbumPressed();
                break;
            }
            default: break;
        }
    }, [goTo])
    // endregion

    // region Render Truck Cover Photo
    const renderTruckCoverLogo = () => {
        const coverHeight = 360;
        return(
            <View style={{ width: '100%' }}>
                {/* Current Cover */}
                <TouchableOpacity onPress={() => setCoverOpened(true)}>
                    <Image
                        source={currentCover ? { uri: currentCover } : require('../../images/image_placeholder_cover.png')}
                        style={{ width: '100%', height: coverHeight }}
                        resizeMode={'cover'}
                    />
                </TouchableOpacity>
            </View>
        );
    }
    // endregion

    // region Render Logo
    const renderLogo = () => {
        const logoSize = 200;
        return(
            <View
                style={{
                    width: logoSize, height: logoSize, backgroundColor: 'white',
                    borderWidth: 1, borderRadius: logoSize / 2, borderColor: 'rgba(0, 0, 0, 0.1)',
                    alignItems: 'center', justifyContent: 'center', overflow: 'hidden'
                }}>
                {/* Current Logo */}
                <TouchableOpacity onPress={() => setLogoOpened(true)}>
                    <Image
                        source={currentLogo ? { uri: currentLogo } : require('../../images/image_placeholder_logo.png')}
                        style={[{ width: logoSize }, !!currentLogo && { height: logoSize }]}
                        resizeMode={currentLogo ? 'cover' : 'contain'}
                    />
                </TouchableOpacity>
            </View>
        );
    }
    // endregion

    // region Render Warning Message
    const renderWarningMessage = () => (
        <TouchableOpacity
            onPress={() => {
                if (warningMessageData.isPendingApproval) {
                    showAlert({ text: 'You can still make changes to your profile, but it won\'t be published until the Truckster team has reviewed it.' });
                }
                else if (warningMessageData.isNoCardAdded) {
                    savedPaymentModal.current?.showModal();
                }
                else if (warningMessageData.isFailedPayment) {
                    savedPaymentModal.current?.showModal(GLOBAL.DATA.USER['tier']);
                }
                else if (warningMessageData.freeTrialData.isFreeTrial) {
                    RootNavigation.jumpTo(RouteKeys.ACCOUNT_SETTINGS_STACK);
                    setTimeout(() => RootNavigation.navigate(RouteKeys.SUBSCRIPTION_TIERS), 500);
                }
            }}
            style={{
                width: '100%', paddingHorizontal: 16, paddingVertical: 16, backgroundColor: '#FFA600',
                flexDirection: 'row', alignItems: 'center'
            }}>
            {/* Info Icon */}
            <AwesomeIcon name={'info-circle'} size={28} color={GLOBAL.COLOR.BASIC.BLACK_TWO} />

            {/* Warning Text */}
            <Text style={{ fontSize: 16, fontWeight: '450', color: GLOBAL.COLOR.BASIC.BLACK_TWO, marginHorizontal: 16, lineHeight: 22, flex: 1 }}>
                {warningMessageData.message}
                {
                    (!!warningMessageData.hasContact) &&
                    <Text style={{ fontSize: 16, fontWeight: 'bold' }}>
                        vendor.support@gotruckster.com{' '}
                        <Text style={{ fontSize: 16, fontWeight: '450' }}>
                            if you have questions or concerns, thank you.
                        </Text>
                    </Text>
                }
            </Text>
        </TouchableOpacity>
    )
    // endregion

    // region Render Braze Banner
    const renderBrazeBanner = () => (
        <BrazeBanner ref={brazeBanner} isLoading={isLoading} />
    );
    // endregion

    // region Render Quick Button
    const renderQuickButton = (text, image, _onPress = () => {}, badgeCount = null) => {
        const imageSize = 28;
        const hasNotice = (badgeCount === '!');
        return(
            <TouchableOpacity
                onPress={_onPress}
                style={{
                    flexDirection: 'row', alignItems: 'center', borderRadius: 6, borderWidth: 2, borderColor: GLOBAL.COLOR.DIVIDER,
                    paddingHorizontal: 20, paddingVertical: 16, marginLeft: 12
                }}>
                <Image
                    source={image}
                    style={{ width: imageSize, height: imageSize }}
                />

                <Text style={{ fontSize: 15, fontWeight: 'bold', color: GLOBAL.COLOR.BASIC.BLACK_TWO, marginLeft: 12 }}>
                    {text}
                </Text>

                {/* Badge Count - This component is inside the Button compo because on Android everything is hidden when off-view */}
                {
                    (!!hasNotice || (badgeCount > 0)) &&
                    <Animatable.View
                        animation={'bounceIn'}
                        style={{
                            backgroundColor: GLOBAL.COLOR.MAIN, marginLeft: 8, paddingBottom: 2,
                            width: 28, height: 28, borderRadius: 14, alignItems: 'center', justifyContent: 'center'
                        }}>
                        <Text style={{ color: 'white', fontSize: 14 }}>
                            {badgeCount || 5}
                        </Text>
                    </Animatable.View>
                }
            </TouchableOpacity>
        );
    };
    // endregion

    // region Render Top Section
    const renderTopSection = () => {
        const truckRating = getRatingValue(truckData);
        const cuisineText = getCuisineText(truckData, 5);
        const textMaxWidth = undefined;
        const maxCityCount = 5;
        return(
            <View style={{ width: '100%', backgroundColor: 'white', padding: 24, flexDirection: 'row', alignItems: 'flex-stat', overflow: 'hidden' }}>
                {/* Logo */}
                {renderLogo()}

                {/* Other Info */}
                <View style={{ flex: 1, marginLeft: 8 }}>
                    {/* Cities / Name / Cuisines / Braze Feed Button */}
                    <View style={{ width: '100%', flexDirection: 'row' }}>
                        {/* Name / Cuisines / Cities */}
                        <View style={{ alignItems: 'flex-start', flex: 1 }}>
                            {/* Name */}
                            <View style={{ marginLeft: 16 }}>
                                {/* Serving Cities */}
                                <Text
                                    adjustsFontSizeToFit={true}
                                    numberOfLines={1}
                                    style={{ fontWeight: '500', color: GLOBAL.COLOR.BASIC.DODGER_BLUE, letterSpacing: 0.4, fontSize: 16, maxWidth: textMaxWidth }}>
                                    {truckData.city.filter((item, index) => index < maxCityCount).map((item) => item.name?.toUpperCase()).join(', ')}
                                    {truckData.city?.length > maxCityCount ? `, +${truckData.city.length - maxCityCount}` : ''}
                                </Text>

                                {/* Truck Name */}
                                <TouchableOpacity onPress={() => editNameModal.current?.showModal(truckData.name)}>
                                    <Text
                                        adjustsFontSizeToFit={true}
                                        numberOfLines={1}
                                        style={{ fontWeight: '900', fontSize: 42, letterSpacing: -0.2, color: GLOBAL.COLOR.BASIC.BLACK_TWO, maxWidth: textMaxWidth, marginVertical: 8 }}>
                                        {truckData.name}
                                    </Text>
                                </TouchableOpacity>

                                {/* Rating and Reviews */}
                                <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                                    {/* Rating */}
                                    <View style={{ flexDirection: 'row', alignItems: 'center', backgroundColor: GLOBAL.COLOR.BASIC.UI_YELLOW, borderRadius: 4, paddingVertical: 4, paddingHorizontal: 6 }}>
                                        <MaterialIcon name={'star'} size={16} color={'white'} />

                                        <Text style={{ fontWeight: 'bold', color: 'white', fontSize: 14, marginLeft: 2 }}>
                                            {truckRating}
                                        </Text>
                                    </View>

                                    {/* Total Reviews and Cuisines */}
                                    <Text style={{ fontSize: 16, color: GLOBAL.COLOR.TEXT_GRAY, marginLeft: 8 }}>
                                        {totalReviewCount} review{totalReviewCount > 1 ? 's' : ''}
                                        {' • ' + cuisineText}
                                    </Text>
                                </View>
                            </View>
                        </View>

                        {/* Braze Feed Button  */}
                        <TouchableOpacity
                            onPress={() => Braze.toggleContentCards()}
                            style={{ alignItems: 'flex-end', justifyContent: 'flex-start', width: 56, height: 56 }}>
                            <MaterialIcon
                                name={'stars'}
                                color={'black'}
                                size={32}
                            />
                        </TouchableOpacity>
                    </View>

                    {/* Quick Buttons */}
                    <View style={{ flexDirection: 'row', alignItems: 'center', marginTop: 24, paddingLeft: 8 }}>
                        {renderQuickButton('Edit Profile', require('../../images/IconLineEdit.svg').default, _onEditProfilePressed)}
                        {renderQuickButton('Schedule', require('../../images/IconLineSchedule.svg').default, () => _onSchedulePressed(false))}
                        {renderQuickButton('Menu', require('../../images/IconLineMenu.svg').default, _onMenuPressed, (!menuOverviewData.length && !!GLOBAL.DATA.USER['squareup_expired_at']) ? '!' : 0)}
                        {renderQuickButton('Reviews', require('../../images/IconLineReview.svg').default, _onReviewPressed, unreadReviewCount)}
                    </View>
                </View>
            </View>
        );
    };
    // endregion

    // region Render Link Item
    const renderLinkItem = (link, icon, type = 'call', linkPrefix = '') => (
        link?.trim() ?
            <TouchableOpacity
                onPress={() => {
                    if (type === 'call') {
                        window.open(`tel:${link}`, '_blank');
                    }
                    else if (type === 'email') {
                        window.open(`mailto:${link}`, '_blank');
                    }
                    else if (type === 'link') {
                        window.open(linkPrefix + link, '_blank');
                    }
                }}
                style={{ flexDirection: 'row', alignItems: 'center', alignSelf: 'stretch', paddingVertical: 16 }}>
                <Image
                    source={icon || require('../../images/icon_facebook.jpeg')}
                    style={{ width: 24, height: 24, tintColor: GLOBAL.COLOR.TEXT_GRAY }}
                />

                <Text style={{ fontSize: 16, color: GLOBAL.COLOR.BASIC.BLACK_TWO, marginLeft: 16 }}>
                    {link?.trim().toLowerCase()}
                </Text>
            </TouchableOpacity>
            :
            null
    )
    // endregion

    // region Render Truck Info
    const renderTruckInfo = () => {
        const truckDescription = truckData.company_description?.trim() || '';
        return(
            <View style={{ flex: 1, height: '100%', backgroundColor: 'white', borderWidth: 1, borderColor: GLOBAL.COLOR.DIVIDER, padding: 32 }}>
                {/* Title */}
                <View style={{ alignSelf: 'stretch', flexDirection: 'row', alignItems: 'center', marginBottom: 16 }}>
                    <Text style={{ fontSize: 32, fontWeight: '900', flex: 1 }}>
                        Truck Info
                    </Text>

                    <TouchableOpacity
                        onPress={_onEditProfilePressed}
                        style={{ width: 40, height: 40, alignItems: 'flex-end', justifyContent: 'center' }}>
                        <Image
                            source={require('../../images/IconLineEdit.svg').default}
                            style={{ width: 32, height: 32, tintColor: GLOBAL.COLOR.BASIC.BLACK_TWO }}
                        />
                    </TouchableOpacity>
                </View>

                {renderLinkItem(truckData.phone?.trim() ? formatPhoneNumber(truckData.phone?.trim()) : '', require('../../images/iconLinePhone@2x.png'), 'call')}
                {renderLinkItem(truckData.contact_email, require('../../images/iconLineMail@2x.png'), 'email')}
                {renderLinkItem(truckData.website_url, require('../../images/iconLineGlobe@2x.png'), 'link')}
                {renderLinkItem(getSocialLinkID(truckData.facebook_url), require('../../images/iconSocialFacebook@2x.png'), 'link', 'https://facebook.com/')}
                {renderLinkItem(getSocialLinkID(truckData.twitter_url), require('../../images/iconSocialTwitter.webp'), 'link', 'https://twitter.com/')}
                {renderLinkItem(getSocialLinkID(truckData.instagram_url), require('../../images/iconSocialInstagram@2x.png'), 'link', 'https://instagram.com/')}

                <Text style={{ fontSize: 24, letterSpacing: -0.2, fontWeight: 'bold', alignSelf: 'stretch', marginTop: 24 }}>
                    Introduction
                </Text>

                {
                    (!!truckDescription?.trim() && truckDescription.toLowerCase() !== 'truckster vendor') ?
                        <Text style={{ marginTop: 16, color: GLOBAL.COLOR.BASIC.BLACK_TWO, fontSize: 16, lineHeight: 26 }} numberOfLines={20}>
                            {truckDescription}
                        </Text>
                        :
                        <Animatable.View
                            animation={'pulse'} easing={'ease-out'} iterationCount={'infinite'}
                            style={{ marginTop: 16 }}>
                            <TouchableOpacity
                                onPress={_onEditProfilePressed}
                                style={{ backgroundColor: GLOBAL.COLOR.BASIC.UI_BLUE, paddingHorizontal: 16, paddingVertical: 20, borderRadius: 6 }}>
                                <Text style={{ color: 'white' }}>
                                    Tell fans about your truck, story, and unique concept here!
                                </Text>
                            </TouchableOpacity>
                        </Animatable.View>
                }
            </View>
        )
    };
    // endregion

    // region Render Schedule Item
    const SCHEDULE_CARD_HEIGHT = 220;
    const renderScheduleItem = (item, index) => {
        const timeObj = item.timeObj;
        return(
            (item.id === -1) ?
                <TouchableOpacity
                    key={'seeAllScheduleButton'}
                    onPress={() => {}}
                    style={{
                        borderWidth: 1, borderRadius: 6, borderColor: GLOBAL.COLOR.BASIC.NEUTRAL_STROKE,
                        padding: 12, alignItems: 'center', justifyContent: 'center',
                        alignSelf: 'stretch', height: SCHEDULE_CARD_HEIGHT, marginHorizontal: 16
                    }}>
                    <Text style={{ color: GLOBAL.COLOR.BASIC.DODGER_BLUE, textAlign: 'center', lineHeight: 20 }}>
                        See All {`\n(${truckCalendar.length})`}
                    </Text>
                </TouchableOpacity>
                :
                <TouchableOpacity
                    key={`openCard_${item.id}`}
                    onPress={() => {}}
                    style={{ alignSelf: 'stretch', height: SCHEDULE_CARD_HEIGHT, marginHorizontal: 16 }}>
                    <View style={{ borderWidth: 1, borderRadius: 6, borderColor: GLOBAL.COLOR.BASIC.NEUTRAL_STROKE, padding: 12, overflow: 'hidden', flex: 1 }}>
                        {/* Time */}
                        <Text style={{ fontSize: 16, color: GLOBAL.COLOR.MAIN, alignSelf: 'stretch', fontWeight: '500' }}>
                            {moment.unix(timeObj.startTime).format('ddd, MMM DD, h:mm A').toUpperCase()}
                        </Text>

                        {/* Event Name */}
                        <Text
                            numberOfLines={1}
                            style={{ fontSize: 20, color: GLOBAL.COLOR.BASIC.BLACK_TWO, alignSelf: 'stretch', marginTop: 32, fontWeight: '500' }}>
                            {item.location_name}
                        </Text>

                        {/* Time String */}
                        <Text style={{ color: GLOBAL.COLOR.TEXT_GRAY, alignSelf: 'stretch', marginTop: 4, fontSize: 16 }}>
                            {timeObj.timeStr}
                        </Text>

                        <View style={{ flex: 1 }}/>

                        {/* Pre-ordering Notice */}
                        {
                            (item['pre_ordering'] === 1) &&
                            <View style={{ paddingHorizontal: 16, paddingVertical: 12, backgroundColor: GLOBAL.COLOR.BASIC.LIGHTER_WHITE, borderRadius: 24, alignSelf: 'flex-start', marginTop: 32 }}>
                                <Text style={{ color: 'black', fontSize: 14 }}>
                                    Pre-ordering: <Text style={{ fontWeight: 'bold', fontSize: 14 }}>ON</Text>
                                </Text>
                            </View>
                        }

                        {/* Warning Icon */}
                        {
                            ((Number(item.latitude) === 0) || (Number(item.latitude) === -1)) &&
                            <TouchableOpacity
                                onPress={() => showAlert({ text: 'Events without an address will not show up on Truckster.<br/><br/>Please add one ASAP.' })}
                                style={{ position: 'absolute', right: 8, top: 8 }}>
                                <MaterialIcon name={'warning'} size={24} color={GLOBAL.COLOR.BASIC.GOLD} />
                            </TouchableOpacity>
                        }
                    </View>
                </TouchableOpacity>
        );
    }
    // endregion

    // region Render Schedule Section
    const renderScheduleSection = () => {
        const displayingScheduleList = truckCalendar
            .filter((item, index) => (index <= 6))
            .concat(truckCalendar.length > 7 ? { id: -1 } : []);

        const truckStatus = truckData.status;
        const leftArrowDisabled = currentCalendarIndex <= 0;
        const rightArrowDisabled = currentCalendarIndex === (displayingScheduleList.length - 1);
        return(
            <View style={{ width: '100%', backgroundColor: 'white', borderWidth: 1, borderColor: GLOBAL.COLOR.DIVIDER, paddingVertical: 32 }}>
                {/* Title */}
                <View style={{ alignSelf: 'stretch', flexDirection: 'row', alignItems: 'center', paddingHorizontal: 32 }}>
                    <Text style={{ fontSize: 32, fontWeight: '900', flex: 1 }}>
                        Schedule
                    </Text>

                    {
                        !isLoadingCalendar &&
                        <TouchableOpacity
                            onPress={() => _onSchedulePressed(false)}
                            style={{
                                flexDirection: 'row', alignItems: 'center', justifyContent: 'center',
                                backgroundColor: GLOBAL.COLOR.BACKGROUND_GREY, borderRadius: 24, paddingHorizontal: 16, paddingVertical: 12
                            }}>
                            <Text style={{ marginRight: 8, fontSize: 14, color: GLOBAL.COLOR.BASIC.BLACK_TWO, fontWeight: 'bold' }}>
                                All Events
                            </Text>

                            <Image
                                source={require('../../images/iconLineArrowBigNext.png')}
                                style={{ width: 14, height: 14, tintColor: GLOBAL.COLOR.BASIC.BLACK_TWO }}
                            />
                        </TouchableOpacity>
                    }
                </View>

                {/* Next 5 - 7 events */}
                {
                    (!isLoadingCalendar && !calendarError && truckCalendar?.length > 0 && (truckStatus === 1 || !truckStatus)) ?
                        <View style={{ alignSelf: 'stretch', flexDirection: 'row', alignItems: 'center', paddingHorizontal: 32 }}>
                            {/* Left Arrow */}
                            <TouchableOpacity
                                disabled={leftArrowDisabled}
                                onPress={() => setCurrentCalendarIndex(currentCalendarIndex - 1)}>
                                <Image
                                    source={require('../../images/iconLineArrowLeft@3x.png')}
                                    style={{ width: 40, height: 40, tintColor: leftArrowDisabled ? GLOBAL.COLOR.BACKGROUND_GREY : GLOBAL.COLOR.BASIC.BLACK_TWO }}
                                />
                            </TouchableOpacity>

                            {/* Main View */}
                            <SwipeableViews
                                style={{ flex: 1, marginTop: 24 }}
                                containerStyle={{ alignSelf: 'stretch', height: SCHEDULE_CARD_HEIGHT }}
                                index={currentCalendarIndex}
                                onChangeIndex={setCurrentCalendarIndex}
                                enableMouseEvents={true}>
                                {displayingScheduleList.map((item, index) => renderScheduleItem(item, index))}
                            </SwipeableViews>

                            {/* Right Arrow */}
                            <TouchableOpacity
                                disabled={rightArrowDisabled}
                                onPress={() => setCurrentCalendarIndex(currentCalendarIndex + 1)}>
                                <Image
                                    source={require('../../images/iconLineArrowRight@3x.png')}
                                    style={{ width: 40, height: 40, tintColor: rightArrowDisabled ? GLOBAL.COLOR.BACKGROUND_GREY : GLOBAL.COLOR.BASIC.BLACK_TWO }}
                                />
                            </TouchableOpacity>
                        </View>
                        :
                        (
                            isLoadingCalendar ?
                                <View style={{ alignItems: 'center', justifyContent: 'center', height: SCHEDULE_CARD_HEIGHT }}>
                                    <LoadingView style={{ backgroundColor: 'white' }}/>
                                </View>
                                :
                                (
                                    calendarError ?
                                        <View style={{ alignItems: 'center', justifyContent: 'center', height: SCHEDULE_CARD_HEIGHT }}>
                                            <Text style={{ color: GLOBAL.COLOR.TEXT_LIGHT_GRAY, textAlign: 'center' }}>
                                                Error while fetching food truck's calendar. Error message: {calendarError}
                                            </Text>
                                        </View>
                                        :
                                        <View style={{ height: SCHEDULE_CARD_HEIGHT, alignSelf: 'stretch', justifyContent: 'center' }}>
                                            {
                                                truckStatus === 1 ?
                                                    <Text style={{ color: GLOBAL.COLOR.BASIC.NEUTRAL_LIGHT_GREY, textAlign: 'center', alignSelf: 'center', lineHeight: 20, marginHorizontal: 24 }}>
                                                        No upcoming events.
                                                        {'\n\n'}Click on Schedule to add open times and locations for consumers to find you.
                                                    </Text>
                                                    :
                                                    <View style={{ alignItems: 'center', justifyContent: 'center', alignSelf: 'stretch', padding: 24, height: SCHEDULE_CARD_HEIGHT }}>
                                                        <Text style={{ color: GLOBAL.COLOR.BASIC.NEUTRAL_GREY, textAlign: 'center', lineHeight: 28 }}>
                                                            {offDateString.current[truckStatus].replace('[date]', moment(truckData['return_date'], 'YYYY-MM-DD').format('MMMM Do, YYYY'))}
                                                        </Text>

                                                        <TouchableOpacity
                                                            onPress={() => _onSchedulePressed(true)}
                                                            style={{ marginTop: 16, paddingHorizontal: 16, paddingVertical: 12, backgroundColor: GLOBAL.COLOR.BACKGROUND_GREY, borderRadius: 6 }}>
                                                            <Text style={{ color: 'black', fontWeight: '500' }}>
                                                                Re-open
                                                            </Text>
                                                        </TouchableOpacity>
                                                    </View>
                                            }
                                        </View>
                                )
                        )
                }
            </View>
        );
    }
    // endregion

    // region Render Menu Item
    const renderMenuItem = (item, index) => {
        const itemCount = Number(item['food_items_count']);
        return(
            (item.id === -1) ?
                <TouchableOpacity
                    key={'seeAllMenuCard'}
                    onPress={() => {}}
                    style={{
                        borderWidth: 1, borderRadius: 6, borderColor: GLOBAL.COLOR.BASIC.NEUTRAL_STROKE,
                        padding: 12, alignItems: 'center', justifyContent: 'center',
                        alignSelf: 'stretch', height: SCHEDULE_CARD_HEIGHT, marginHorizontal: 16
                    }}>
                    <Text style={{ color: GLOBAL.COLOR.BASIC.DODGER_BLUE, textAlign: 'center', lineHeight: 20 }}>
                        See All {`\n(${menuOverviewData?.length})`}
                    </Text>
                </TouchableOpacity>
                :
                <TouchableOpacity
                    key={`menuCard_${item.id}`}
                    onPress={() => {}}
                    style={{ alignSelf: 'stretch', height: SCHEDULE_CARD_HEIGHT, marginHorizontal: 16 }}>
                    <View style={{ borderWidth: 1, borderRadius: 6, borderColor: GLOBAL.COLOR.BASIC.NEUTRAL_STROKE, padding: 16, overflow: 'hidden', flex: 1 }}>
                        {/* Category Name */}
                        <Text style={{ fontSize: 20, color: GLOBAL.COLOR.BASIC.BLACK_TWO, alignSelf: 'stretch', fontWeight: 'bold', marginTop: 4, marginBottom: 16 }}>
                            {item.name}
                        </Text>

                        {/* Food Items */}
                        {
                            (item['first_three_items'] || []).length === 0 ?
                                <Text style={{ color: GLOBAL.COLOR.TEXT_GRAY, alignSelf: 'stretch', marginTop: 6, fontSize: 16 }}>
                                    No food item in this category
                                </Text>
                                :
                                (item['first_three_items'] || []).map((foodItem, index) => (
                                    <View
                                        key={`foodItem_${foodItem.name}_${index}`}
                                        style={{ flexDirection: 'row', alignItems: 'center', paddingVertical: 2, marginTop: 8 }}>
                                        <Text style={{ color: GLOBAL.COLOR.BASIC.BLACK_TWO, flex: 1, fontSize: 17 }}>
                                            {foodItem.name}
                                        </Text>

                                        <Text style={{ color: GLOBAL.COLOR.BASIC.BLACK_TWO, fontSize: 17 }}>
                                            ${foodItem.price}
                                        </Text>
                                    </View>
                                ))
                        }

                        {/* Food Count */}
                        {
                            itemCount > 3 &&
                            <Text style={{ color: GLOBAL.COLOR.TEXT_GRAY, alignSelf: 'stretch', marginTop: 8, fontSize: 16 }}>
                                + {itemCount - 3} more item{(itemCount - 3) > 1 ? 's' : ''}
                            </Text>
                        }
                    </View>
                </TouchableOpacity>
        );
    }
    // endregion

    // region Render Menu Section
    const renderMenuSection = () => {
        const displayingMenuList = menuOverviewData
            .filter((item, index) => (index <= 6))
            .concat(menuOverviewData.length > 7 ? { id: -1 } : []);

        const leftArrowDisabled = currentMenuIndex <= 0;
        const rightArrowDisabled = currentMenuIndex === (displayingMenuList.length - 1);

        return(
            <View style={{ width: '100%', backgroundColor: 'white', borderWidth: 1, borderColor: GLOBAL.COLOR.DIVIDER, paddingVertical: 32, marginTop: 24 }}>
                {/* Title */}
                <View style={{ alignSelf: 'stretch', flexDirection: 'row', alignItems: 'center', paddingHorizontal: 32 }}>
                    <Text style={{ fontSize: 32, fontWeight: '900', flex: 1 }}>
                        Menu Overview
                    </Text>

                    {
                        !isLoadingMenuOverview &&
                        <TouchableOpacity
                            onPress={_onMenuPressed}
                            style={{
                                flexDirection: 'row', alignItems: 'center', justifyContent: 'center',
                                backgroundColor: GLOBAL.COLOR.BACKGROUND_GREY, borderRadius: 24, paddingHorizontal: 16, paddingVertical: 12
                            }}>
                            <Text style={{ marginRight: 8, fontSize: 14, color: GLOBAL.COLOR.BASIC.BLACK_TWO, fontWeight: 'bold' }}>
                                Full Menu
                            </Text>

                            <Image
                                source={require('../../images/iconLineArrowBigNext.png')}
                                style={{ width: 14, height: 14, tintColor: GLOBAL.COLOR.BASIC.BLACK_TWO }}
                            />
                        </TouchableOpacity>
                    }
                </View>

                {/* Menu Overview */}
                {
                    isLoadingMenuOverview ?
                        <View style={{ alignSelf: 'stretch', height: SCHEDULE_CARD_HEIGHT, alignItems: 'center', justifyContent: 'center' }}>
                            <LoadingView />
                        </View>
                        :
                        (
                            menuOverviewError ?
                                <View style={{ alignSelf: 'stretch', height: SCHEDULE_CARD_HEIGHT, alignItems: 'center', justifyContent: 'center' }}>
                                    <Text style={{ color: GLOBAL.COLOR.TEXT_GRAY, textAlign: 'center', marginHorizontal: 48 }}>
                                        {menuOverviewError?.toString()}
                                    </Text>
                                </View>
                                :
                                (
                                    (menuOverviewData.length === 0) ?
                                        <View style={{ height: SCHEDULE_CARD_HEIGHT, alignSelf: 'stretch', padding: 24, alignItems: 'center', justifyContent: 'center' }}>
                                            <Text style={{ textAlign: 'center', color: GLOBAL.COLOR.BASIC.NEUTRAL_LIGHT_GREY, lineHeight: 28 }}>
                                                Your truck has no food items on your menu yet,
                                                {'\n'}tap the above button to add some!
                                            </Text>
                                        </View>
                                        :
                                        <View style={{ alignSelf: 'stretch', flexDirection: 'row', alignItems: 'center', paddingHorizontal: 32 }}>
                                            {/* Left Arrow */}
                                            <TouchableOpacity
                                                disabled={leftArrowDisabled}
                                                onPress={() => setCurrentMenuIndex(currentMenuIndex - 1)}>
                                                <Image
                                                    source={require('../../images/iconLineArrowLeft@3x.png')}
                                                    style={{ width: 40, height: 40, tintColor: leftArrowDisabled ? GLOBAL.COLOR.BACKGROUND_GREY : GLOBAL.COLOR.BASIC.BLACK_TWO }}
                                                />
                                            </TouchableOpacity>

                                            {/* Main View */}
                                            <SwipeableViews
                                                style={{ flex: 1, marginTop: 24 }}
                                                containerStyle={{ alignSelf: 'stretch', height: SCHEDULE_CARD_HEIGHT }}
                                                index={currentMenuIndex}
                                                onChangeIndex={setCurrentMenuIndex}
                                                enableMouseEvents={true}>
                                                {displayingMenuList.map((item) => renderMenuItem(item))}
                                            </SwipeableViews>

                                            {/* Right Arrow */}
                                            <TouchableOpacity
                                                disabled={rightArrowDisabled}
                                                onPress={() => setCurrentMenuIndex(currentMenuIndex + 1)}>
                                                <Image
                                                    source={require('../../images/iconLineArrowRight@3x.png')}
                                                    style={{ width: 40, height: 40, tintColor: rightArrowDisabled ? GLOBAL.COLOR.BACKGROUND_GREY : GLOBAL.COLOR.BASIC.BLACK_TWO }}
                                                />
                                            </TouchableOpacity>
                                        </View>
                                )
                        )
                }
            </View>
        )
    }
    // endregion

    // region Render Photo Section
    const renderPhotoSection = () => {
        const displayingPhotoList = photoData.filter((item, index) => (index <= 2));
        const remainingPhotoCount = totalPhotoResult - displayingPhotoList.length;
        return(
            <View style={{ width: '100%', backgroundColor: 'white', borderWidth: 1, borderColor: GLOBAL.COLOR.DIVIDER, paddingTop: 32, marginTop: 24 }}>
                {/* Title */}
                <View style={{ alignSelf: 'stretch', flexDirection: 'row', alignItems: 'center', paddingHorizontal: 32 }}>
                    <Text style={{ fontSize: 32, fontWeight: '900', flex: 1 }}>
                        Photos
                    </Text>

                    {
                        !isLoadingPhotos &&
                        <TouchableOpacity
                            onPress={_onAlbumPressed}
                            style={{
                                flexDirection: 'row', alignItems: 'center', justifyContent: 'center',
                                backgroundColor: GLOBAL.COLOR.BACKGROUND_GREY, borderRadius: 24, paddingHorizontal: 16, paddingVertical: 12
                            }}>
                            <Text style={{ marginRight: 8, fontSize: 14, color: GLOBAL.COLOR.BASIC.BLACK_TWO, fontWeight: 'bold' }}>
                                Open Album
                            </Text>

                            <Image
                                source={require('../../images/iconLineArrowBigNext.png')}
                                style={{ width: 14, height: 14, tintColor: GLOBAL.COLOR.BASIC.BLACK_TWO }}
                            />
                        </TouchableOpacity>
                    }
                </View>

                {/* Photos */}
                {
                    isLoadingPhotos ?
                        <View style={{ alignSelf: 'stretch', height: SCHEDULE_CARD_HEIGHT, alignItems: 'center', justifyContent: 'center', marginTop: 32 }}>
                            <LoadingView />
                        </View>
                        :
                        (
                            !!photoError?.trim() ?
                                <View style={{ height: SCHEDULE_CARD_HEIGHT, alignSelf: 'stretch', alignItems: 'center', justifyContent: 'center' }}>
                                    <Text style={{ fontSize: 18, textAlign: 'center', color: GLOBAL.COLOR.TEXT_GRAY }}>
                                        Failed to load truck photos, please try again later.
                                        {'\n\n'}
                                        Error message:
                                        {'\n'}
                                        {photoError}
                                    </Text>
                                </View>
                                :
                                (
                                    photoData.length > 0 ?
                                        <View style={{
                                            height: SCHEDULE_CARD_HEIGHT, alignSelf: 'stretch', flexDirection: 'row', alignItems: 'center',
                                            marginTop: 32, overflow: 'hidden'
                                        }}>
                                            {displayingPhotoList.map((item, index) => (
                                                (index <= 2) &&
                                                <TouchableOpacity
                                                    key={`photoItem_${item.id}`}
                                                    onPress={() => {
                                                        setCurrentPhotoIndex(index);
                                                        setPhotoCarouselOpened(true);
                                                    }}
                                                    style={{ flex: 1, height: SCHEDULE_CARD_HEIGHT }}>
                                                    <Image
                                                        key={item.key}
                                                        source={{ uri: item.src }}
                                                        style={{ flex: 1 }}
                                                        resizeMode={'cover'}
                                                    />

                                                    {/* Last Item Overlay */}
                                                    {
                                                        ((index === displayingPhotoList.length - 1) && (remainingPhotoCount > 0)) &&
                                                        <View style={{ position: 'absolute', left: 0, right: 0, bottom: 0, top: 0, backgroundColor: 'rgba(0, 0, 0, 0.5)', alignItems: 'center', justifyContent: 'center' }}>
                                                            <Text style={{ color: 'white', fontWeight: '500', fontSize: 32 }}>
                                                                +{remainingPhotoCount}
                                                            </Text>
                                                        </View>
                                                    }
                                                </TouchableOpacity>
                                            ))}
                                        </View>
                                        :
                                        <View style={{ height: SCHEDULE_CARD_HEIGHT, alignSelf: 'stretch', padding: 24, paddingTop: 8, alignItems: 'center', justifyContent: 'center' }}>
                                            <Text style={{ textAlign: 'center', color: GLOBAL.COLOR.BASIC.NEUTRAL_LIGHT_GREY, lineHeight: 28 }}>
                                                Your truck has no photos,
                                                {'\n'}go to your album to add some!
                                            </Text>
                                        </View>
                                )
                        )
                }
            </View>
        );
    };
    // endregion

    // region Render Rating Item
    const renderRatingItem = (starCount, reviewCount) => (
        <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'flex-end', marginBottom: 8 }}>
            {/* Stars */}
            <StarRatings
                rating={starCount}
                starRatedColor={GLOBAL.COLOR.BASIC.UI_YELLOW}
                starEmptyColor={GLOBAL.COLOR.BASIC.PINKISH_GREY}
                numberOfStars={5}
                starDimension={'16px'}
                starSpacing={'1px'}
            />

            {/* Progress Bar */}
            <ProgressBar
                color={GLOBAL.COLOR.MAIN}
                trackColor={GLOBAL.COLOR.BASIC.LIGHT_WHITE}
                style={{ flex: 1, height: 16, marginLeft: 12, borderRadius: 8 }}
                progress={(Number(reviewCount) > 0) ? (Number(reviewCount) / totalReviewCount) : 0}
            />

            {/* Review Count */}
            <Text style={{ color: GLOBAL.COLOR.BASIC.WARM_GREY, width: 32, textAlign: 'right', fontSize: 12 }}>
                {reviewCount}
            </Text>
        </View>
    );
    // endregion

    // region Render Review Section
    const renderReviewSection = () => {
        const reviewSummary = truckData['reviews_summary'] || {};
        const truckRating = getRatingValue(truckData);
        const displayingReviewData = reviewData.filter((item, index) => index < 2);
        return(
            <View style={{ alignSelf: 'stretch', margin: 24, backgroundColor: 'white', borderWidth: 1, borderColor: GLOBAL.COLOR.DIVIDER, padding: 32 }}>
                {
                    !!reviewError?.trim() ?
                        <View style={{ alignSelf: 'stretch', height: SCHEDULE_CARD_HEIGHT, alignItems: 'center', justifyContent: 'center' }}>
                            <Text style={{ fontSize: 18, textAlign: 'center', color: GLOBAL.COLOR.TEXT_GRAY }}>
                                Failed to load truck reviews, please try again later.
                                {'\n\n'}
                                Error message:
                                {'\n'}
                                {reviewError}
                            </Text>
                        </View>
                        :
                        <View style={{ flex: 1, flexDirection: 'row', alignItems: 'flex-start' }}>
                            {/* Rating Info */}
                            <View style={{ width: '30%' }}>
                                {/* Title */}
                                <Text style={{ fontSize: 32, fontWeight: '900', marginBottom: 32 }}>
                                    Rating
                                </Text>

                                {/* Content */}
                                <View style={{ alignSelf: 'stretch', alignItems: 'center', justifyContent: 'flex-start' }}>
                                    {/* Average Rating */}
                                    <Text style={{ fontSize: 48, fontWeight: 'bold', letterSpacing: 0.5 }}>
                                        {Number(truckRating || 0).toFixed(1)}
                                    </Text>

                                    {/* Stars */}
                                    <StarRatings
                                        rating={truckRating}
                                        starRatedColor={GLOBAL.COLOR.MAIN}
                                        starEmptyColor={GLOBAL.COLOR.BASIC.PINKISH_GREY}
                                        numberOfStars={5}
                                        starDimension={'32px'}
                                        starSpacing={'1px'}
                                    />

                                    {/* Review Count */}
                                    <Text style={{ color: GLOBAL.COLOR.BASIC.WARM_GREY, fontSize: 16, marginTop: 8 }}>
                                        {totalReviewCount} review{totalReviewCount > 1 ? 's' : ''}
                                    </Text>

                                    {/* All Review Stars */}
                                    <View style={{ alignSelf: 'stretch', marginTop: 48 }}>
                                        {renderRatingItem(5, reviewSummary['5stars'])}
                                        {renderRatingItem(4, reviewSummary['4stars'])}
                                        {renderRatingItem(3, reviewSummary['3stars'])}
                                        {renderRatingItem(2, reviewSummary['2stars'])}
                                        {renderRatingItem(1, reviewSummary['1stars'])}
                                    </View>
                                </View>
                            </View>

                            {/* Newest Reviews */}
                            <View style={{ flex: 1, marginLeft: 64 }}>
                                {/* Title */}
                                <View style={{ alignSelf: 'stretch', flexDirection: 'row', alignItems: 'center', marginBottom: 8 }}>
                                    <Text style={{ fontSize: 32, fontWeight: '900', flex: 1 }}>
                                        Latest Reviews
                                    </Text>

                                    {
                                        !isLoadingReviews &&
                                        <TouchableOpacity
                                            onPress={_onReviewPressed}
                                            style={{
                                                flexDirection: 'row', alignItems: 'center', justifyContent: 'center',
                                                backgroundColor: GLOBAL.COLOR.BACKGROUND_GREY, borderRadius: 24, paddingHorizontal: 16, paddingVertical: 12
                                            }}>
                                            <Text style={{ marginRight: 8, fontSize: 14, color: GLOBAL.COLOR.BASIC.BLACK_TWO, fontWeight: 'bold' }}>
                                                View All Reviews
                                            </Text>

                                            <Image
                                                source={require('../../images/iconLineArrowBigNext.png')}
                                                style={{ width: 14, height: 14, tintColor: GLOBAL.COLOR.BASIC.BLACK_TWO }}
                                            />
                                        </TouchableOpacity>
                                    }
                                </View>

                                {/* Reviews */}
                                {
                                    isLoadingReviews ?
                                        <View style={{ alignSelf: 'stretch', flex: 1, alignItems: 'center', justifyContent: 'center' }}>
                                            <LoadingView />
                                        </View>
                                        :
                                        <View style={{ alignSelf: 'stretch', flex: 1 }}>
                                            {displayingReviewData.map((review, index) => (
                                                <View key={`reviewItem_${review.id}`} style={{ alignSelf: 'stretch', borderColor: GLOBAL.COLOR.DIVIDER, borderBottomWidth: index === 0 ? 1 : 0, paddingVertical: 24 }}>
                                                    {/* User Info */}
                                                    <View style={{ flexDirection: 'row', alignItems: 'flex-start', alignSelf: 'stretch' }}>
                                                        {/* Avatar */}
                                                        <Image
                                                            source={review.user?.avatar ? { uri: review.user?.avatar } : require('../../images/image_user_placeholder_round.png')}
                                                            style={{ width: 64, height: 64, borderRadius: 32, backgroundColor: GLOBAL.COLOR.TEXT_LIGHT_GRAY }}
                                                            resizeMode={'cover'}
                                                        />

                                                        {/* Name and Rating */}
                                                        <View style={{ flex: 1, marginLeft: 16 }}>
                                                            {/* Name */}
                                                            <Text style={{ fontSize: 18, fontWeight: 'bold' }}>
                                                                {review.user?.name}{(GLOBAL.DATA.USER && (reviewData.user?.email === GLOBAL.DATA.USER.email)) ? ' (You)' : ''}
                                                            </Text>

                                                            {/* Rating and Date */}
                                                            <View style={{ flexDirection: 'row', alignItems: 'center', marginTop: 8 }}>
                                                                {/* Stars */}
                                                                <StarRatings
                                                                    rating={Number(review.rating)}
                                                                    starRatedColor={GLOBAL.COLOR.MAIN}
                                                                    starEmptyColor={GLOBAL.COLOR.BASIC.PINKISH_GREY}
                                                                    numberOfStars={5}
                                                                    starDimension={'18px'}
                                                                    starSpacing={'1px'}
                                                                />

                                                                {/* Date */}
                                                                <Text style={{ color: GLOBAL.COLOR.TEXT_GRAY, fontSize: 14, lineHeight: 18, marginLeft: 6 }}>
                                                                    {moment.utc(review['created_at']).fromNow()}
                                                                    {(review['updated_at'] && (review['created_at'] !== review['updated_at'])) ? `(edited)` : ''}
                                                                </Text>
                                                            </View>
                                                        </View>
                                                    </View>

                                                    {/* Review Text */}
                                                    <Text style={{ color: GLOBAL.COLOR.BASIC.BLACK_TWO, alignSelf: 'stretch', marginTop: 24, lineHeight: 28 }}>
                                                        {review.comment?.trim()}
                                                    </Text>
                                                </View>
                                            ))}
                                        </View>
                                }
                            </View>
                        </View>
                }
            </View>
        );
    }
    // endregion

    // region MAIN RENDER
    return(
        <View style={{ width: '100%', flex: 1, backgroundColor: '#EDEDED' }}>
            {
                (isLoading && !truckDataLoadedBefore.current) ?
                    <LoadingView isLoading={true} />
                    :
                    (
                        !!errorMessage?.trim() ?
                            <Animatable.View
                                animation={'fadeIn'} duration={500}
                                style={{ flex: 1, width: '100%', alignItems: 'center', justifyContent: 'center' }}>
                                <Text style={{ fontSize: 18, textAlign: 'center', color: GLOBAL.COLOR.TEXT_GRAY }}>
                                    Failed to load truck profile, please try again later.
                                    {'\n\n'}
                                    Error message:
                                    {'\n'}
                                    {errorMessage}
                                </Text>
                            </Animatable.View>
                            :
                            <Animatable.View
                                animation={'fadeIn'} duration={500}
                                style={{ flex: 1, width: '100%' }}>
                                {/* Warning Message */}
                                {!!warningMessageData.message?.trim() && renderWarningMessage()}

                                {/* Main Content */}
                                <ScrollView style={{ flex: 1, width: '100%' }}>
                                    {/* Braze Content Card */}
                                    {!warningMessageData.message?.trim() && renderBrazeBanner()}

                                    {/* Cover and Top Section */}
                                    <View style={{ alignSelf: 'stretch', margin: 24, backgroundColor: 'white', borderWidth: 1, borderColor: GLOBAL.COLOR.DIVIDER }}>
                                        {/* Cover Photo */}
                                        {renderTruckCoverLogo()}

                                        {/* Top Section */}
                                        {renderTopSection()}
                                    </View>

                                    {/* Truck Info / Schedule / Menu / Photos */}
                                    <View style={{ alignSelf: 'stretch', marginHorizontal: 24, flexDirection: 'row', alignItems: 'flex-start' }}>
                                        {/* Truck Info */}
                                        {renderTruckInfo()}

                                        {/* Other Sections */}
                                        <View style={{ flex: 1, marginLeft: 24 }}>
                                            {/* Schedule */}
                                            {renderScheduleSection()}

                                            {/* Menu Overview */}
                                            {renderMenuSection()}

                                            {/* Photos */}
                                            {renderPhotoSection()}
                                        </View>
                                    </View>

                                    {/* Review Section */}
                                    {renderReviewSection()}
                                </ScrollView>

                                {/* Edit Name Modal */}
                                <EditNameModal
                                    ref={editNameModal}
                                    truckData={truckData}
                                    onChangeCompleted={(newName) => {
                                        setTruckData(Object.assign(cloneDeep(truckData), { name: newName }));
                                        setGlobalTruckData(Object.assign(cloneDeep(globalTruckData), { name: newName }))
                                    }}
                                />

                                {/* Saved Payment Modal */}
                                <SavedPayment ref={savedPaymentModal} />
                            </Animatable.View>
                    )
            }

            {/* Lightbox - Cover Photo */}
            <Lightbox
                plugins={[Captions]}
                open={coverOpened}
                close={() => setCoverOpened(false)}
                slides={[{ src: currentCover, title: 'Truck Cover Photo', description: 'To update your cover photo,\ngo to Edit Profile' }]}
                carousel={{ imageFit: 'contain', finite: true }}
                styles={{ container: { backgroundColor: 'rgba(0, 0, 0, 0.8)' } }}
                render={{ buttonPrev: () => false, buttonNext: () => false }}
                captions={{ descriptionTextAlign: 'center' }}
            />

            {/* Lightbox - Logo Photo */}
            <Lightbox
                plugins={[Captions]}
                open={logoOpened}
                close={() => setLogoOpened(false)}
                slides={[{ src: currentLogo, title: 'Truck Logo Photo', description: 'To update your logo photo,\ngo to Edit Profile' }]}
                carousel={{ imageFit: 'contain', finite: true }}
                styles={{ container: { backgroundColor: 'rgba(0, 0, 0, 0.8)' } }}
                render={{ buttonPrev: () => false, buttonNext: () => false }}
                captions={{ descriptionTextAlign: 'center' }}
            />

            {/* Lightbox - Truck Photos */}
            <Lightbox
                plugins={[Captions]}
                open={photoCarouselOpened}
                close={() => setPhotoCarouselOpened(false)}
                index={currentPhotoIndex}
                on={{ view: ({ index }) => setCurrentPhotoIndex(index) }}
                slides={photoData}
                carousel={{ imageFit: 'contain' }}
                styles={{ container: { backgroundColor: 'rgba(0, 0, 0, 0.8)' } }}
            />
        </View>
    );
    // endregion
}
